/**
 * Action Center read / cleared state (2026-09-16 redesign).
 *
 * Offices choose how clearing works with the `ACTION_CENTER_DISMISS_SCOPE` setting
 * (Settings → General, admin only):
 *   'office'     — anyone clearing an alert clears it for everyone. DEFAULT: absent/unknown ⇒ office,
 *                  for existing and new offices alike.
 *   'individual' — clearing hides it only for the person who cleared it.
 *
 * The WRITE path is identical in both modes — clearing always records "who, when"
 * (`bot-events.readBy.{employeeID}` = ms, `conversations.agentDismissedBy.{employeeID}` = ms) — so
 * switching the setting needs no migration; only these READ predicates change.
 *
 * Needs-action cards that were handled (plan question answered, new payment link sent) are
 * `status:'resolved'` on the doc and hidden for everyone regardless of the setting.
 *
 * Import-free: cypress/node-tests/action-center-read-state.test.js executes this exact file.
 */

export const DISMISS_SCOPE_SETTING = 'ACTION_CENTER_DISMISS_SCOPE';
export const DISMISS_SCOPE_OFFICE = 'office';
export const DISMISS_SCOPE_INDIVIDUAL = 'individual';

/** 'individual' only when explicitly set; anything else (absent, blank, typo) ⇒ 'office'. */
export function resolveDismissScope(value) {
  const v = value === null || value === undefined ? '' : String(value).trim().toLowerCase();
  return v === DISMISS_SCOPE_INDIVIDUAL ? DISMISS_SCOPE_INDIVIDUAL : DISMISS_SCOPE_OFFICE;
}

/** Scope from `customer.settings` (array of {Property, PropertyValue}) or an allSettings-style map. */
export function dismissScopeFromSettings(settings) {
  if (Array.isArray(settings)) {
    const row = settings.find((s) => s && s.Property === DISMISS_SCOPE_SETTING);
    return resolveDismissScope(row && row.PropertyValue);
  }
  if (settings && typeof settings === 'object') {
    const row = settings[DISMISS_SCOPE_SETTING];
    return resolveDismissScope(row && typeof row === 'object' ? row.PropertyValue : row);
  }
  return DISMISS_SCOPE_OFFICE;
}

function plainMap(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}

function hasEntry(v) {
  return v !== null && v !== undefined && v !== false && v !== '';
}

function hasEmployee(employeeID) {
  return employeeID !== null && employeeID !== undefined && employeeID !== '';
}

/** Handled by someone (resolve event) — hidden for everyone, whatever the setting. */
export function isBotEventResolved(evt) {
  if (!evt) return true;
  if (evt.resolvedAt) return true;
  return typeof evt.status === 'string' && evt.status !== 'pending';
}

/** This employee cleared it: new `readBy` map, or the legacy `acknowledgedBy` array (pre-redesign docs). */
export function hasEmployeeRead(evt, employeeID) {
  if (!evt || !hasEmployee(employeeID)) return false;
  const key = String(employeeID);
  const readBy = plainMap(evt.readBy);
  if (Object.prototype.hasOwnProperty.call(readBy, key) && hasEntry(readBy[key])) return true;
  const acked = Array.isArray(evt.acknowledgedBy) ? evt.acknowledgedBy : [];
  return acked.some((id) => hasEntry(id) && String(id) === key);
}

/** Anyone in the office cleared it. */
export function hasAnyoneRead(evt) {
  if (!evt) return false;
  const readBy = plainMap(evt.readBy);
  if (Object.keys(readBy).some((k) => hasEntry(readBy[k]))) return true;
  return Array.isArray(evt.acknowledgedBy) && evt.acknowledgedBy.some(hasEntry);
}

/**
 * Should this bot-event card be hidden for the viewer?
 * Individual mode with no employee id fails OPEN (shows the card) — hiding patient activity on a
 * broken auth state is worse than showing one extra card.
 */
export function isBotEventCleared(evt, { employeeID, scope } = {}) {
  if (isBotEventResolved(evt)) return true;
  return resolveDismissScope(scope) === DISMISS_SCOPE_INDIVIDUAL
    ? hasEmployeeRead(evt, employeeID)
    : hasAnyoneRead(evt);
}

/**
 * Should this SMS alert be hidden? A dismissal only covers messages up to the moment it was made:
 * when the patient writes again (`lastMessageTime` > dismissal) the alert comes back.
 * `convo` is an intake object from actionCenterSms.intakeConversation.
 */
export function isSmsAlertCleared(convo, { employeeID, scope } = {}) {
  if (!convo) return true;
  const lastMessageTime = Number(convo.lastMessageTime);
  if (!Number.isFinite(lastMessageTime) || lastMessageTime <= 0) return false;
  const dismissedBy = plainMap(convo.agentDismissedBy);
  const covers = (at) => {
    const n = Number(at);
    return hasEntry(at) && Number.isFinite(n) && n > 0 && lastMessageTime <= n;
  };
  if (resolveDismissScope(scope) === DISMISS_SCOPE_INDIVIDUAL) {
    if (!hasEmployee(employeeID)) return false;
    return covers(dismissedBy[String(employeeID)]);
  }
  return Object.keys(dismissedBy).some((k) => covers(dismissedBy[k]));
}
